import React, { useEffect, useState, useContext } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import * as endPoints from '../assets/js/endpoints';
import Loader from '../components/loader';
import { MONTHS, MONTH_LABELS } from '../constants/months';
import { UserContext } from '../assets/contexts/userContext';
import { getEmployeeColor } from '../constants/employeeColors';
import { useNotification } from '../assets/contexts/notificationContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const EmployeeStats = () => {
  const [clientStats, setClientStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState([
    new Date().toISOString().slice(5, 7),
  ]);
  const { user } = useContext(UserContext);
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await endPoints.getTopClientsMonthly();
        setClientStats(data);
      } catch (_err) {
        showNotification('Error obteniendo estadísticas', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <Loader />;

  const clientTotals = {};
  selectedMonths.forEach(m => {
    Object.entries(clientStats).forEach(([key, list]) => {
      if (key.slice(5, 7) === m) {
        list.forEach(c => {
          clientTotals[c.client_name] = (clientTotals[c.client_name] || 0) + c.total;
        });
      }
    });
  });

  const topData = Object.entries(clientTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const chartData = {
    labels: topData.map(d => d[0]),
    datasets: [{
      label: 'Visitas',
      backgroundColor: user.color || getEmployeeColor(user.id),
      data: topData.map(d => d[1])
    }]
  };

  const hasData = topData.length > 0;

  return (
    <Grid container spacing={1} sx={{ mt: 7, alignItems: 'center', justifyContent: 'center' }}>
      <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Autocomplete
          multiple
          options={MONTHS}
          value={selectedMonths}
          onChange={(_event, newValue) => setSelectedMonths(newValue)}
          getOptionLabel={option => MONTH_LABELS[option]}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} />
              <ListItemText primary={MONTH_LABELS[option]} />
            </li>
          )}
          renderInput={params => (
            <TextField {...params} label="Meses" sx={{ minWidth: '12rem' }} />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ mt: 1, width: '100%' }}>
          <CardHeader title="Top clientes" subheader="más visitas" />
          <CardContent>
            {hasData ? (
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: false,
                  normalized: true,
                  scales: {
                    x: { alignToPixels: true },
                    y: { alignToPixels: true, beginAtZero: true },
                  },
                }}
                style={{ width: '100%', height: 300 }}
              />
            ) : (
              <Typography>No hay citas en el mes seleccionado</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default EmployeeStats;
